import { useQuery } from '@apollo/client'
import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    Stack,
    TextField,
    Typography,
} from '@mui/material'
import Link from '@tiptap/extension-link'
import Mention from '@tiptap/extension-mention'
import Underline from '@tiptap/extension-underline'
import StarterKit from '@tiptap/starter-kit'
import { useFormik } from 'formik'
import {
    LinkBubbleMenu,
    LinkBubbleMenuHandler,
    MenuButtonBold,
    MenuButtonBulletedList,
    MenuButtonEditLink,
    MenuButtonItalic,
    MenuButtonOrderedList,
    MenuButtonUnderline,
    MenuControlsContainer,
    MenuDivider,
    MenuSelectHeading,
    RichTextEditor,
    type RichTextEditorRef,
} from 'mui-tiptap'

import { useRef, useState } from 'react'
import DrawerComponent from 'shared/components/DrawerComponent'
import MenuButton from 'shared/components/MenuButton/MenuButton'
import { getPxAsRem } from 'shared/utils'
import { GetJobBoardOptions, GetMergeFields } from '../BulkActions.gql'
import { validationSchema, initialValues } from './blastValidationSchema'
import EmailPreferenceOrder from './EmailPreferenceOrder'
import JobBoardForm from './JobBoardForm'
import { makeMergeFieldInserter } from './makeMergeUtils'
import UnreachableCandidatesDrawer from './UnreachableCandidatesDrawer'
import { useBlastRosterCandidates } from './useBlastRosterCandidates'
import { useSendTestEmail } from './useSendTestEmail'

interface RosterBlasterProps {
    candidateIds: string[]
    rosterId: string
    isOpen: boolean
    onClose: () => void
}

const RosterBlaster = ({
    candidateIds,
    rosterId,
    isOpen,
    onClose,
}: RosterBlasterProps) => {
    const rteRef = useRef<RichTextEditorRef>(null)
    const [unreachableData, setUnreachableData] = useState<any[]>([])

    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit: () => {
            blastRosterCandidates()
        },
    })

    /* options for job board dropdowns */
    const { data: jobBoardOptions } = useQuery(GetJobBoardOptions, {
        variables: { rosterId },
    })

    const titles =
        jobBoardOptions?.roster?.placements
            ?.map((p) => p.title)
            .filter((v) => v !== undefined && v !== null) || []
    const sectors =
        jobBoardOptions?.roster?.sectors
            ?.map((s) => s.sector)
            .filter((v) => v !== undefined && v !== null) || []
    const locations =
        jobBoardOptions?.roster?.companyOffices
            ?.map((c) =>
                c.companyOffice?.officeLocation?.value && c.companyOffice?.id
                    ? {
                          value: c.companyOffice?.officeLocation?.value,
                          id: c.companyOffice?.id,
                      }
                    : null
            )
            .filter((v) => v !== null) || []
    const startDate =
        jobBoardOptions?.roster?.startDates
            ?.map((sd) => sd.startDateLabel)
            .join(', ') || ''

    const { data } = useQuery(GetMergeFields)

    const insertMergeField = makeMergeFieldInserter(rteRef.current?.editor)

    /* what happens when you hit the cancel button */
    const handleCancel = (data?: any) => {
        if (Array.isArray(data) && data.length > 0) {
            setUnreachableData(data)
            formik.resetForm()
        } else {
            onClose()
            rteRef?.current?.editor?.commands.clearContent()
            formik.resetForm()
            setUnreachableData([])
        }
    }

    /* what happens when you hit the send button */
    const blastRosterCandidates = useBlastRosterCandidates(
        rteRef.current?.editor,
        data,
        candidateIds,
        rosterId,
        formik.values,
        handleCancel
    )

    /* what happens when you hit the send test button */
    const sendTestEmail = useSendTestEmail(
        rteRef.current?.editor,
        data,
        formik.values
    )

    if (unreachableData.length > 0) {
        return (
            <UnreachableCandidatesDrawer
                isOpen={isOpen}
                unreachableData={unreachableData}
                onClose={() => handleCancel()}
            />
        )
    }

    return (
        <>
            <DrawerComponent
                open={isOpen}
                hasCancelButton
                onPrimaryAction={formik.handleSubmit}
                primaryActionText={`Send (${candidateIds.length})`}
                onCancel={handleCancel}
                anchor="right"
                onClose={handleCancel}
                headerTitle="Create New Blast"
                width={getPxAsRem(600)}
            >
                <Typography variant="h5">Subject</Typography>
                <TextField
                    fullWidth
                    required
                    label="Subject"
                    name="subject"
                    value={formik.values.subject}
                    error={
                        formik.touched.subject && Boolean(formik.errors.subject)
                    }
                    helperText={formik.touched.subject && formik.errors.subject}
                    onChange={formik.handleChange}
                    sx={{
                        marginY: getPxAsRem(16),
                    }}
                />

                <Box
                    sx={{
                        '.tipTapMenu': {
                            justifyContent: 'space-between',
                        },
                        '.ProseMirror': {
                            minHeight: getPxAsRem(200),
                        },
                    }}
                >
                    <Typography
                        sx={{
                            marginY: getPxAsRem(16),
                        }}
                        variant="h5"
                    >
                        Email Content
                    </Typography>
                    <RichTextEditor
                        ref={rteRef}
                        extensions={[
                            StarterKit.configure({
                                heading: {
                                    levels: [1, 2, 3],
                                },
                                hardBreak: { keepMarks: true },
                            }),
                            Underline,
                            Link.configure({
                                openOnClick: false,
                                autolink: true,
                            }),
                            LinkBubbleMenuHandler,
                            Mention.configure({
                                renderText({ options, node }) {
                                    return `{{${node.attrs.label ?? node.attrs.id}}}`
                                },
                                HTMLAttributes: {
                                    class: 'mention',
                                },
                                deleteTriggerWithBackspace: true,
                            }),
                        ]}
                        renderControls={(editor) => (
                            <MenuControlsContainer className="tipTapMenu">
                                <MenuSelectHeading />
                                <MenuDivider />
                                <MenuButtonBold />
                                <MenuButtonItalic />
                                <MenuButtonUnderline />
                                <MenuButtonEditLink />
                                <MenuButtonBulletedList />
                                <MenuButtonOrderedList />
                                <MenuButton
                                    label="Merge Field"
                                    variant="outlined"
                                    size="small"
                                    menuItems={
                                        data?.mergeFields.map(
                                            ({ label, mergeKey }) => ({
                                                label,
                                                onClick: () =>
                                                    insertMergeField(mergeKey),
                                            })
                                        ) || []
                                    }
                                />
                            </MenuControlsContainer>
                        )}
                    >
                        {() => <LinkBubbleMenu />}
                    </RichTextEditor>
                </Box>

                <Stack direction="row" spacing={2} marginTop={3}>
                    <Button
                        variant="outlined"
                        onClick={sendTestEmail}
                        sx={{ width: 'fit-content' }}
                    >
                        Send Test Email
                    </Button>
                </Stack>

                <EmailPreferenceOrder
                    value={formik.values.emailPreferenceOrder}
                    onChange={(newOrder) =>
                        formik.setFieldValue('emailPreferenceOrder', newOrder)
                    }
                />

                <FormControlLabel
                    control={
                        <Checkbox
                            checked={formik.values.jobBoard}
                            onChange={(e) =>
                                formik.setFieldValue(
                                    'jobBoard',
                                    e.target.checked
                                )
                            }
                        />
                    }
                    sx={{ marginTop: getPxAsRem(16), display: 'none' }}
                    label="Send To Job Board"
                />
                {formik.values.jobBoard && (
                    <JobBoardForm
                        formik={formik}
                        titles={titles}
                        sectors={sectors}
                        locations={locations}
                        startDate={startDate}
                    />
                )}
            </DrawerComponent>
        </>
    )
}

export default RosterBlaster
